/**
 * Zebra Browser Print transport.
 *
 * Talks directly to the Browser Print agent's localhost HTTP API (the same
 * API Zebra's bundled JS SDK wraps — the SDK is not CDN-distributed, so we
 * use the endpoints it uses: GET /available, GET /default, POST /write).
 * The agent must be installed on the machine and the site accepted in its
 * allowed-origins prompt; see /inventory/printing for setup notes.
 *
 * Nothing here runs at import time — no network cost outside printing flows.
 */

// http on loopback is exempt from mixed-content blocking in Chrome/Edge;
// the https variant (self-signed localhost cert) is the fallback for
// browsers that do block it.
const AGENT_BASES = ['http://127.0.0.1:9100', 'https://localhost:9101'];
const DETECT_TIMEOUT_MS = 3000;
const WRITE_TIMEOUT_MS = 15000;

const PRINTER_STORAGE_KEY = 'prism.labelPrinter.printerUid';
const DPI_STORAGE_KEY = 'prism.labelPrinter.dpi';
export const SUPPORTED_DPIS = [203, 300];
export const DEFAULT_DPI = 203;

export class LabelPrinterError extends Error {
    constructor(message, code) {
        super(message);
        this.name = 'LabelPrinterError';
        this.code = code;
    }
}

export const ERROR_CODES = {
    AGENT_NOT_RUNNING: 'agent-not-running',
    PRINTER_NOT_FOUND: 'printer-not-found',
    PRINT_FAILED: 'print-failed'
};

// Resolved agent base URL, cached per page load once detected.
let agentBase = null;

/**
 * Brave blocks web pages from reaching localhost by default (per-site
 * "Localhost access" permission), which makes a running Browser Print agent
 * look absent. Brave exposes navigator.brave; presence is a reliable signal.
 */
export function isBraveBrowser() {
    return typeof navigator !== 'undefined' && Boolean(navigator.brave);
}

/** True when the Browser Print agent is reachable on this machine. */
export async function detectAgent() {
    return (await resolveAgentBase()) !== null;
}

/** All Zebra printers the agent can see (USB + network). */
export async function listPrinters() {
    const base = await resolveAgentBase();
    if (!base) {
        throw new LabelPrinterError('Zebra Browser Print is not running', ERROR_CODES.AGENT_NOT_RUNNING);
    }
    const response = await fetchWithTimeout(`${base}/available`, {}, DETECT_TIMEOUT_MS);
    if (!response.ok) {
        throw new LabelPrinterError(`Browser Print /available failed (${response.status})`, ERROR_CODES.AGENT_NOT_RUNNING);
    }
    const data = await response.json();
    return data?.printer || [];
}

/** The agent's configured default printer, or null if it has none. */
export async function getDefaultPrinter() {
    const base = await resolveAgentBase();
    if (!base) {
        throw new LabelPrinterError('Zebra Browser Print is not running', ERROR_CODES.AGENT_NOT_RUNNING);
    }
    try {
        const response = await fetchWithTimeout(`${base}/default?type=printer`, {}, DETECT_TIMEOUT_MS);
        if (!response.ok) return null;
        const text = await response.text();
        if (!text) return null;
        const device = JSON.parse(text);
        return device?.uid ? device : null;
    } catch {
        return null;
    }
}

/**
 * Send raw ZPL to the chosen printer. Resolution order: the printer saved in
 * this browser (if still attached) → the agent's default → the only attached
 * printer. Rejects with a typed LabelPrinterError the UI can branch on:
 * agent-not-running → show the download fallback; printer-not-found /
 * print-failed → tell the operator exactly what went wrong.
 */
export async function printZpl(zpl) {
    const base = await resolveAgentBase();
    if (!base) {
        throw new LabelPrinterError('Zebra Browser Print is not running', ERROR_CODES.AGENT_NOT_RUNNING);
    }

    const printer = await choosePrinter();
    if (!printer) {
        throw new LabelPrinterError('No Zebra printer found — check the USB cable and power', ERROR_CODES.PRINTER_NOT_FOUND);
    }

    let response;
    try {
        response = await fetchWithTimeout(`${base}/write`, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify({ device: printer, data: zpl })
        }, WRITE_TIMEOUT_MS);
    } catch (e) {
        throw new LabelPrinterError(`Send to printer failed: ${e.message}`, ERROR_CODES.PRINT_FAILED);
    }
    if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new LabelPrinterError(
            `Printer rejected the job (${response.status}${detail ? `: ${detail}` : ''})`,
            ERROR_CODES.PRINT_FAILED
        );
    }
}

// ============================================================================
// Per-browser preferences (warehouse machines may have several Zebras)
// ============================================================================

export function getSavedPrinterUid() {
    return localStorage.getItem(PRINTER_STORAGE_KEY) || null;
}

export function savePrinterUid(uid) {
    if (uid) localStorage.setItem(PRINTER_STORAGE_KEY, uid);
    else localStorage.removeItem(PRINTER_STORAGE_KEY);
}

export function getSavedDpi() {
    const dpi = parseInt(localStorage.getItem(DPI_STORAGE_KEY) || '', 10);
    return SUPPORTED_DPIS.includes(dpi) ? dpi : DEFAULT_DPI;
}

export function saveDpi(dpi) {
    if (SUPPORTED_DPIS.includes(dpi)) localStorage.setItem(DPI_STORAGE_KEY, String(dpi));
}

// ============================================================================
// Internals
// ============================================================================

async function resolveAgentBase() {
    if (agentBase) return agentBase;
    for (const base of AGENT_BASES) {
        try {
            const response = await fetchWithTimeout(`${base}/available`, {}, DETECT_TIMEOUT_MS);
            if (response.ok) {
                agentBase = base;
                return base;
            }
        } catch {
            // agent not listening on this base — try the next
        }
    }
    return null;
}

async function choosePrinter() {
    const printers = await listPrinters();
    const savedUid = getSavedPrinterUid();
    if (savedUid) {
        const saved = printers.find(p => p.uid === savedUid);
        if (saved) return saved;
    }
    const fallback = await getDefaultPrinter();
    if (fallback) return fallback;
    return printers[0] || null;
}

async function fetchWithTimeout(url, options, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        return await fetch(url, { ...options, signal: controller.signal });
    } finally {
        clearTimeout(timer);
    }
}

/** Test hook: clear the cached agent base between tests. */
export function __resetAgentCacheForTests() {
    agentBase = null;
}
